import { Calendar, LogOut, User, Shield, Sun, Moon, Monitor } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../auth';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SidebarTrigger } from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const YEARS = Array.from({ length: 16 }, (_, i) => 2020 + i);

export function Header() {
  const { state, setYear } = useApp();
  const { user, logout, isHR } = useAuth();
  const { theme, setTheme } = useTheme();

  const cycleTheme = () => {
    if (theme === 'light') setTheme('dark');
    else if (theme === 'dark') setTheme('system');
    else setTheme('light');
  };

  const ThemeIcon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;
  const themeLabel = theme === 'light' ? 'Hell' : theme === 'dark' ? 'Dunkel' : 'System';

  return (
    <header className="sticky top-0 z-40 w-full bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-white/10 h-14 no-print">
      <div className="flex items-center justify-between h-full px-4 lg:px-6">
        {/* Left: Sidebar trigger + Logo */}
        <div className="flex items-center gap-2">
          <SidebarTrigger className="hidden lg:flex" />
          <div className="p-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <Calendar className="w-5 h-5 text-gray-900 dark:text-gray-50" />
          </div>
          <span className="text-lg font-semibold text-gray-900 dark:text-gray-50">Stundenliste</span>
        </div>

        {/* Right: Year, Theme, User */}
        <div className="flex items-center gap-3">
          {/* Year selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:block">
              Jahr:
            </span>
            <Select
              value={String(state.year)}
              onValueChange={(v) => setYear(parseInt(v, 10))}
            >
              <SelectTrigger className="w-auto h-9 px-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map((year) => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Theme Toggle */}
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={cycleTheme}
                  variant="ghost"
                  size="icon"
                  aria-label={`Theme: ${themeLabel}`}
                >
                  <ThemeIcon className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Theme: {themeLabel}</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* User Dropdown */}
          {user && (
            <div className="pl-3 border-l border-gray-200 dark:border-white/10">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full" aria-label="Benutzermenü">
                    <User className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-medium flex items-center gap-1">
                        {user.name}
                        {isHR && <Shield className="w-3.5 h-3.5 text-amber-500" aria-label="HR-Zugang" />}
                      </p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
